import { gsap, CSSPlugin } from 'gsap';
import { ScrollToPlugin } from 'gsap/all';

gsap.registerPlugin(CSSPlugin);
gsap.registerPlugin(ScrollToPlugin);

/**
 * Opens an element
 *
 * @example
 * import {open} from '../helpers';
 *
 * open({ element: this.el.querySelector('.element)});
 *
 * @param {Object} element - HTML element you want to target for animation
 * @param {Number} duration - Speed of the animation
 * @param {String} ease - Ease of the animation
 * @param {Function} onComplete - On complete function
 */

type openProps = {
  element: HTMLElement;
  duration?: number;
  ease?: string;
  onComplete?: () => void;
  height?: string;
};

export const open = ({
  element,
  duration = 0.4,
  ease = 'easeOut',
  onComplete,
  height = 'auto'
}: openProps) => {
  gsap.set(element, { height });
  gsap.from(element, {
    height: 0,
    ease: ease,
    duration,
    onComplete: () => {
      onComplete && onComplete();
    }
  });
};

/**
 * Closes an element
 *
 * @example
 * import {close} from '../helpers';
 *
 * close({ element: this.el.querySelector('.element)});
 *
 * @param {Object} element - HTML element you want to target for animation
 * @param {Number} duration - Speed of the animation
 * @param {String} ease - Ease of the animation
 * @param {Function} onComplete - On complete function
 */

type closeProps = {
  element: HTMLElement;
  duration?: number;
  ease?: string;
  onComplete?: () => void;
};

export const close = ({ element, duration = 0.4, ease = 'easeOut', onComplete }: closeProps) => {
  gsap.set(element, { height: element.offsetHeight });
  gsap.to(element, {
    height: 0,
    ease: ease,
    duration,
    onComplete: () => {
      onComplete && onComplete();
    }
  });
};

// /**
// * Checks browser is running on a native mobile device
// */

type ExtendedWindowType = typeof window & {
  opera: string;
  MSStream: boolean;
};
export const isDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as ExtendedWindowType).opera;

  if (
    /windows phone/i.test(userAgent) ||
    /android/i.test(userAgent) ||
    (/iPad|iPhone|iPod/.test(userAgent) && !(window as ExtendedWindowType).MSStream)
  ) {
    return true;
  }

  return false;
};

// eslint-disable-next-line
export const setTrackDataLayer: (data: { eventName: string, custom: any }) => void = ({ eventName, custom = {} }) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...custom
  });
};

export const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) {
    return parts.pop()?.split(';').shift();
  } else {
    return false;
  }
};


export const recommendArticlePost = (endpoint: string, data: string) => {
  fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
    },
    body: data
  }).then(response => response.json())
    .then(data => {
      console.log(data);
    }).catch(err => {
      console.error(err);
    });
}


export const addInnerContainer = () => {
  const innerContainer = document.querySelector('.main-content:has(.sidebar)');


  if (innerContainer && !(innerContainer as HTMLElement).dataset.disableInnerContainer) {
    innerContainer.classList.add('inner_container');
  }
};

export const hideEmptySidebars = () => {
  const sidebars = document.querySelectorAll('.sidebar');

  sidebars.forEach((sidebar) => {
    const managedContentBlocks = sidebar.querySelectorAll('.sidebar__content .sidebar__content');

    if (!managedContentBlocks.length) {
      return;
    }

    const hasManagedContent = Array.from(managedContentBlocks).some((contentBlock) => {
      const contentElement = contentBlock as HTMLElement;
      const hasElementChildren = contentElement.children.length > 0;
      const normalizedText = contentElement.textContent?.replace(/\u00a0/g, ' ').trim();
      const hasTextContent = !!normalizedText;

      return hasElementChildren || hasTextContent;
    });

    if (!hasManagedContent) {
      const sidebarElement = sidebar as HTMLElement;
      sidebarElement.hidden = true;
      sidebarElement.classList.add('sidebar--hidden');
      sidebarElement.style.setProperty('display', 'none', 'important');
    }
  });
};

type BrightcoveEmbedData = {
  accountId: string;
  videoId: string;
  player?: string;
  embed?: string;
};

type BrightcoveWindowType = typeof window & {
  bc?: (el?: Element) => unknown;
};

/**
 * Builds the Brightcove advanced (in-page) embed markup used by the GTM plugin
 */
export const buildBrightcoveEmbed = ({
  accountId,
  videoId,
  player = 'default',
  embed = 'default'
}: BrightcoveEmbedData) => `
  <video
    data-account="${accountId}"
    data-player="${player}"
    data-embed="${embed}"
    data-video-id="${videoId}"
    class="video-js"
    controls></video>
`;

/**
 * Extracts account/video ids from a Brightcove iframe URL
 */
export const parseBrightcoveIframeUrl = (videoUrl: string): BrightcoveEmbedData | null => {
  try {
    const url = new URL(videoUrl, window.location.origin);

    if (!url.hostname.endsWith('players.brightcove.net')) {
      return null;
    }

    const [accountId, playerEmbed] = url.pathname.split('/').filter(Boolean);
    const videoId = url.searchParams.get('videoId');

    if (!accountId || !videoId) {
      return null;
    }

    const [player = 'default', embed = 'default'] = (playerEmbed || '').split('_');

    return { accountId, videoId, player, embed };
  } catch {
    return null;
  }
};

/**
 * Initializes Brightcove embeds injected after page load
 */
export const initBrightcovePlayers = (scope: ParentNode = document) => {
  const embeds = Array.from(scope.querySelectorAll<HTMLElement>('video-js, video.video-js'));

  if (!embeds.length) {
    return;
  }

  const { bc } = window as BrightcoveWindowType;

  if (bc) {
    embeds.forEach(embed => bc(embed));
    return;
  }

  const { account, player = 'default', embed = 'default' } = embeds[0].dataset;
  const src = `https://players.brightcove.net/${account}/${player}_${embed}/index.min.js`;

  if (document.querySelector(`script[src="${src}"]`)) {
    return;
  }

  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  document.body.appendChild(script);
};

/**
 * Stops any media inside a container that is being closed or hidden
 */
export const pauseMediaIn = (scope: ParentNode | null) => {
  if (!scope) {
    return;
  }

  scope.querySelectorAll<HTMLMediaElement>('video, audio').forEach(media => {
    if (!media.paused) {
      media.pause();
    }
  });
};

export const addVideoModal = () => {
  const MODAL_DIALOG_PATH = '/sitedata/components/modaldialog/';
  const modalLinks = Array.from(document.querySelectorAll('a.launch-media_modal')) as HTMLAnchorElement[];

  const isModalDialogPath = (path: string) => path.includes(MODAL_DIALOG_PATH);

  const getPathname = (href: string) => {
    try {
      return new URL(href, window.location.origin).pathname;
    } catch {
      return '';
    }
  };

  const getModalHashPath = () => {
    const hash = window.location.hash;

    if (!hash || !hash.startsWith('#/')) {
      return '';
    }

    try {
      return decodeURIComponent(hash.replace('#', ''));
    } catch {
      return '';
    }
  };

  const openModalFromHref = (
    href: string,
    options: {
      setHash?: boolean;
      scrollTarget?: HTMLElement | null;
      navigateOnError?: boolean;
    } = {}
  ) => {
    const { setHash = true, scrollTarget = null, navigateOnError = false } = options;
    const url = new URL(href, window.location.origin);
    url.searchParams.set('type', 'modal');

    fetch(url.toString(), {
      headers: {
        'Accept': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Modal endpoint responded with ${response.status}`);
        }

        return response.json();
      })
      .then(data => {
        const modalContent = data.modal;

        if (!modalContent) {
          return;
        }

        const existingModal = document.querySelector('dialog.modal--media') as HTMLDialogElement | null;
        if (existingModal) {
          if (existingModal.open) {
            existingModal.close();
          }
          existingModal.remove();
        }

        // Create new modal
        const modal = document.createElement('dialog');
        modal.className = 'modal modal--media';
        modal.setAttribute('data-module', 'modal');
        modal.setAttribute('data-modal-id', `modal-${modalContent.id}`);

        const brightcoveVideo = modalContent.video_url
          ? parseBrightcoveIframeUrl(modalContent.video_url)
          : modalContent.video_id
            ? { accountId: modalContent.video_account, videoId: modalContent.video_id }
            : null;

        // Create modal template
        const modalTemplate = `
            <div class="modal__container">
              <div class="modal__header">
                <button class="modal__close" type="button" aria-label="Close modal">
                  <svg class="modal__close-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </div>
              <div class="modal__content rtf">
                ${modalContent.title ? `<h6>${modalContent.title}</h6>` : ''}
                ${modalContent.headline ? `<h2>${modalContent.headline}</h2>` : ''}

                ${brightcoveVideo ? `
                  <div class="media_container video">
                    ${buildBrightcoveEmbed(brightcoveVideo)}
                  </div>
                ` : modalContent.video_url ? `
                  <div class="media_container video">
                    <iframe src="${modalContent.video_url}" scrolling="no" allowfullscreen="" webkitallowfullscreen="" mozallowfullscreen=""></iframe>
                  </div>
                ` : modalContent.image_url ? `
                  <div class="media_container">
                    <img src="${modalContent.image_url}" alt="">
                  </div>
                ` : ''}

                ${modalContent.content || modalContent.file_url ? `
                  <div class="col2">
                    ${modalContent.content || ''}
                    ${modalContent.file_url ? `
                      <div style="margin-top: 30px;">
                        <a class="btn" href="${modalContent.file_url}" target="_blank">
                          ${modalContent.cta_label}
                        </a>
                      </div>
                    ` : ''}
                  </div>
                ` : ''}

                ${modalContent.additional_content ? `
                  <div class="additional_content">
                    ${modalContent.additional_content}
                  </div>
                ` : ''}
              </div>
            </div>
          `;

        // Add modal to DOM
        modal.innerHTML = modalTemplate;
        document.body.appendChild(modal);

        if (brightcoveVideo) {
          initBrightcovePlayers(modal);
        }

        let hasClosed = false;
        const handleModalClosed = () => {
          if (hasClosed) {
            return;
          }
          hasClosed = true;
          // Stop playback explicitly; detaching the dialog alone is not enough
          // to guarantee the audio stops
          pauseMediaIn(modal);
          modal.remove();
          history.pushState('', document.title, window.location.pathname + window.location.search);
        };

        // Add click handlers for closing
        const closeModal = () => {
          if (modal.open) {
            modal.close();
          }

          // `close` does not fire reliably across engines, so the teardown is
          // driven here; hasClosed keeps the event path from repeating it
          handleModalClosed();
        };

        modal.addEventListener('close', handleModalClosed);
        // Escape fires `cancel` first; handling it means the teardown no longer
        // depends on `close` alone, which is unreliable across engines
        modal.addEventListener('cancel', handleModalClosed);
        modal.addEventListener('click', (event) => {
          const rect = modal.getBoundingClientRect();
          const isInDialog = (
            rect.top <= event.clientY &&
            event.clientY <= rect.top + rect.height &&
            rect.left <= event.clientX &&
            event.clientX <= rect.left + rect.width
          );
          if (!isInDialog) {
            closeModal();
          }
        });

        const closeButton = modal.querySelector('.modal__close');
        if (closeButton) {
          closeButton.addEventListener('click', closeModal);
        }

        if (setHash) {
          window.location.hash = url.pathname;
        }

        if (scrollTarget) {
          scrollTarget.scrollIntoView({ block: 'center' });
        }

        modal.showModal();

        if (scrollTarget) {
          requestAnimationFrame(() => {
            scrollTarget.scrollIntoView({ block: 'center' });
          });
        }
      })
      .catch(error => {
        console.error('Error loading modal content:', error);

        // The default navigation was already prevented, so without this the
        // click is simply dead whenever the endpoint is unreachable
        if (navigateOnError) {
          window.location.assign(href);
        }
      });
  };

  const modalHashPath = getModalHashPath();
  if (!modalLinks.length && !isModalDialogPath(modalHashPath)) {
    return;
  }

  modalLinks.forEach(link => {
    link.addEventListener('click', (e: Event) => {
      e.preventDefault();
      const currentLink = e.currentTarget as HTMLAnchorElement;
      openModalFromHref(currentLink.href, { navigateOnError: true });
    });
  });

  if (!isModalDialogPath(modalHashPath)) {
    return;
  }

  const matchingLink = modalLinks.find(link => getPathname(link.href) === modalHashPath);

  openModalFromHref(matchingLink?.href || modalHashPath, {
    setHash: false,
    scrollTarget: matchingLink ?? null
  });
};

/**
 * Creates and displays a confirmation modal
 *
 * @example
 * import {createConfirmationModal} from '../helpers';
 *
 * createConfirmationModal({
 *   content: '<p>Are you sure?</p>',
 *   cancelLabel: 'Cancel',
 *   confirmLabel: 'Buy Now',
 *   confirmUrl: 'https://example.com'
 * });
 *
 * @param {Object} options - Modal configuration options
 * @param {string} options.content - HTML content to display in the modal
 * @param {string} options.cancelLabel - Label for the cancel button
 * @param {string} options.confirmLabel - Label for the confirm/buy now button
 * @param {string} options.confirmUrl - URL to navigate to when confirm button is clicked
 */
type ConfirmationModalProps = {
  content: string;
  cancelLabel?: string;
  confirmLabel?: string;
  confirmUrl: string;
};

export const createConfirmationModal = ({
  content,
  cancelLabel = 'Cancel',
  confirmLabel = 'Buy Now',
  confirmUrl
}: ConfirmationModalProps) => {
  // Create new modal
  const modal = document.createElement('dialog');
  modal.className = 'modal';
  modal.setAttribute('data-module', 'modal');
  modal.setAttribute('data-modal-id', `modal-confirmation-${Date.now()}`);

  // Create modal template
  const modalTemplate = `
    <div class="modal__container">
      <div class="modal__header">
        <button class="modal__close" type="button" aria-label="Close modal">
          <svg class="modal__close-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
      <div class="modal__content rtf">
        ${content}
        <div class="modal__actions" style="margin-top: 30px; display: flex; gap: 15px; justify-content: flex-end;">
          <button class="btn btn--secondary modal__cancel" type="button">${cancelLabel}</button>
          <a class="btn btn--primary modal__confirm" href="${confirmUrl}" target="_blank">${confirmLabel}</a>
        </div>
      </div>
    </div>
  `;

  // Add modal to DOM
  modal.innerHTML = modalTemplate;
  document.body.appendChild(modal);

  // Add click handlers for closing
  const closeModal = () => {
    modal.close();
    modal.remove(); // Remove from DOM when closed
  };

  modal.addEventListener('click', (event) => {
    const rect = modal.getBoundingClientRect();
    const isInDialog = (
      rect.top <= event.clientY &&
      event.clientY <= rect.top + rect.height &&
      rect.left <= event.clientX &&
      event.clientX <= rect.left + rect.width
    );
    if (!isInDialog) {
      closeModal();
    }
  });

  const closeButton = modal.querySelector('.modal__close');
  if (closeButton) {
    closeButton.addEventListener('click', closeModal);
  }

  const cancelButton = modal.querySelector('.modal__cancel');
  if (cancelButton) {
    cancelButton.addEventListener('click', closeModal);
  }

  modal.showModal();
};
